/**
 * CLIENT-SIDE SERVER SESSION MANAGEMENT
 *
 * This module provides client-side integration with the server-side session API.
 * It handles session creation, validation, storage, and automatic refresh.
 *
 * How it works with Vercel:
 * 1. On login: Client calls /api/auth/sessions to create a session stored in Supabase
 * 2. Session ID is stored in localStorage and sent with every API request
 * 3. Server validates session on each request via the sessions API
 * 4. On logout: Session is revoked server-side, preventing any further use
 * 5. Sessions expire after 24 hours or 2 hours of inactivity
 *
 * Security benefits:
 * - Sessions can be revoked server-side (e.g., admin force logout)
 * - Session data stored in database, not just client
 * - Prevents session hijacking by tracking IP/user agent
 * - Limits concurrent sessions per user
 */

import { API_BASE_URL } from '../constants';

/**
 * SECURITY NOTE (N7 — v2.9.33):
 * Session IDs are currently stored in localStorage because this is a Vercel-hosted SPA
 * without a traditional server-rendered cookie flow. In a future architecture update,
 * consider migrating to httpOnly secure cookies set by the API layer to prevent session
 * theft via XSS. This would require a BFF (Backend-For-Frontend) or middleware layer to
 * issue Set-Cookie headers on the same origin.
 */
const SESSION_STORAGE_KEY = 'aureus_server_session_id';
const SESSION_EXPIRY_KEY = 'aureus_server_session_expiry';

interface SessionCreateResponse {
 success: boolean;
 sessionId?: string;
 expiresAt?: string;
 message?: string;
 error?: string;
}

interface SessionValidateResponse {
 valid: boolean;
 userId?: number;
 userEmail?: string;
 loginMethod?: string;
 telegramId?: number;
 createdAt?: string;
 expiresAt?: string;
 needsRenewal?: boolean;
 error?: string;
}

/**
 * Get the API base URL
 */
function getApiBaseUrl(): string {
 // Use the centralized API_BASE_URL from constants
 // This handles both development (localhost:8002) and production (relative URLs)
 return API_BASE_URL;
}

/**
 * Create a new server-side session after successful login
 */
export async function createServerSession(
 userId: number,
 userEmail: string,
 loginMethod: 'email' | 'telegram' = 'email',
 telegramId?: number,
	 loginToken?: string,
	 recoveryAuthToken?: string | null
): Promise<{ success: boolean; sessionId?: string; error?: string }> {
 try {
 console.log('Creating server-side session for user:', userId);

	 const headers: Record<string, string> = {
	  'Content-Type': 'application/json',
	 };
	 if (!loginToken && recoveryAuthToken) {
	  headers.Authorization = `Bearer ${recoveryAuthToken}`;
	 }

	 const response = await fetch(`${getApiBaseUrl()}/api/auth/sessions`, {
 method: 'POST',
	 headers,
 body: JSON.stringify({
  userId,
  userEmail,
  loginMethod,
  telegramId,
  ...(loginToken ? { loginToken } : {}),
 }),
 });

 const data: SessionCreateResponse = await response.json();

 if (!response.ok || !data.success) {
 console.error('Failed to create server session:', data.error);
 return { success: false, error: data.error || 'Failed to create session' };
 }

 // Store session ID in localStorage
 if (data.sessionId && data.expiresAt) {
 localStorage.setItem(SESSION_STORAGE_KEY, data.sessionId);
 localStorage.setItem(SESSION_EXPIRY_KEY, data.expiresAt);
 console.log('Server session created and stored');
 }

 return { success: true, sessionId: data.sessionId };
 } catch (error) {
 console.error('Error creating server session:', error);
 return { success: false, error: 'Network error creating session' };
 }
}

/**
 * Validate current session with the server
 */
export async function validateServerSession(): Promise<SessionValidateResponse> {
 try {
 const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

 if (!sessionId) {
 return { valid: false, error: 'No session ID stored' };
 }

 // Quick client-side expiry check
 const expiresAt = localStorage.getItem(SESSION_EXPIRY_KEY);
 if (expiresAt && new Date(expiresAt) < new Date()) {
 console.log('⏰ Session expired (client-side check)');
 clearServerSession();
 return { valid: false, error: 'Session expired' };
 }

 const response = await fetch(`${getApiBaseUrl()}/api/auth/sessions`, {
 method: 'GET',
 headers: {
 'X-Session-Id': sessionId,
 },
 });

 const data: SessionValidateResponse = await response.json();

 if (!response.ok || !data.valid) {
 console.warn('Server session validation failed:', data.error);
 clearServerSession();
 return { valid: false, error: data.error };
 }

 // Update expiry if session was renewed
 if (data.expiresAt) {
 localStorage.setItem(SESSION_EXPIRY_KEY, data.expiresAt);
 }

 console.log('Server session validated');
 return data;
 } catch (error) {
 console.error('Error validating server session:', error);
 return { valid: false, error: 'Network error validating session' };
 }
}

/**
 * Revoke current session (logout)
 */
export async function revokeServerSession(): Promise<{ success: boolean; error?: string }> {
 try {
 const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

 if (!sessionId) {
 // No session to revoke, still clear local storage
 clearServerSession();
 return { success: true };
 }

 const response = await fetch(`${getApiBaseUrl()}/api/auth/sessions`, {
 method: 'DELETE',
 headers: {
 'X-Session-Id': sessionId,
 },
 });

 // Clear local storage regardless of server response
 clearServerSession();

 if (!response.ok) {
 const data = await response.json();
 console.warn('Server session revocation returned error:', data.error);
 // Still return success since we cleared local storage
 }

 console.log('Server session revoked');
 return { success: true };
 } catch (error) {
 console.error('Error revoking server session:', error);
 // Still clear local storage on error
 clearServerSession();
 return { success: true }; // Consider it successful since local is cleared
 }
}

/**
 * Revoke all sessions for a user (security action)
 */
export async function revokeAllServerSessions(userId: number): Promise<{ success: boolean; revokedCount?: number; error?: string }> {
 try {
 console.log('Revoking all sessions for user:', userId);

 const response = await fetch(`${getApiBaseUrl()}/api/auth/sessions?all=true`, {
 method: 'DELETE',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ userId }),
 });

 const data = await response.json();

 // Clear local storage
 clearServerSession();

 if (!response.ok) {
 console.error('Failed to revoke all sessions:', data.error);
 return { success: false, error: data.error };
 }

 console.log(`Revoked ${data.revokedCount} sessions for user ${userId}`);
 return { success: true, revokedCount: data.revokedCount };
 } catch (error) {
 console.error('Error revoking all sessions:', error);
 clearServerSession();
 return { success: false, error: 'Network error revoking sessions' };
 }
}

/**
 * Clear server session from localStorage
 */
export function clearServerSession(): void {
 localStorage.removeItem(SESSION_STORAGE_KEY);
 localStorage.removeItem(SESSION_EXPIRY_KEY);
 console.log('Server session cleared from localStorage');
}

/**
 * Get current session ID
 */
export function getServerSessionId(): string | null {
 return localStorage.getItem(SESSION_STORAGE_KEY);
}

/**
 * Check if we have a stored session (quick check, no validation)
 */
export function hasServerSession(): boolean {
 const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
 const expiresAt = localStorage.getItem(SESSION_EXPIRY_KEY);

 if (!sessionId) return false;
 if (expiresAt && new Date(expiresAt) < new Date()) {
 clearServerSession();
 return false;
 }

 return true;
}

/**
 * Add session ID to fetch headers for authenticated requests
 */
export function getSessionHeaders(): Record<string, string> {
 const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
 return sessionId ? { 'X-Session-Id': sessionId } : {};
}

/**
 * Async version: ensures a valid session exists before returning headers.
 * If the session is missing or expired, auto-creates one using the current Supabase auth user.
 * Falls back to empty headers if session creation fails.
 */
export async function ensureSessionHeaders(): Promise<Record<string, string>> {
 // Fast path: session already exists and not expired
 if (hasServerSession()) {
 const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
 if (sessionId) return { 'X-Session-Id': sessionId };
 }

 // Session missing or expired - try to create one
 try {
	 const { getCurrentUser, getCurrentUserToken } = await import('./supabase');
 const currentUser = await getCurrentUser();
 if (currentUser?.email) {
 const dbUserId = currentUser.database_user?.id || currentUser.id;
 if (dbUserId) {
 console.log('ensureSessionHeaders: Auto-creating session for user:', dbUserId);
	 const authToken = await getCurrentUserToken();
	 const result = await createServerSession(Number(dbUserId), currentUser.email, 'email', undefined, undefined, authToken);
 if (result.success && result.sessionId) {
 console.log('ensureSessionHeaders: Session created:', result.sessionId.substring(0, 8) + '...');
 return { 'X-Session-Id': result.sessionId };
 }
 }
 }
 } catch (err) {
 console.warn('ensureSessionHeaders: Auto-session creation failed:', err);
 }

 // Last resort: return whatever we have (may be empty)
 return getSessionHeaders();
}

export default {
 createServerSession,
 validateServerSession,
 revokeServerSession,
 revokeAllServerSessions,
 clearServerSession,
 getServerSessionId,
 hasServerSession,
 getSessionHeaders,
 ensureSessionHeaders,
};
